import React from 'react'
import { TextareaAutosize } from '@mui/base'
import { ArchIntake } from 'apis/entities/architecture.entity'
import { InlineEditButtons } from 'components/InlineEditControls'
import Svg, { Icon } from 'components/Svg'
import ButtonSpinner from './ButtonSpinner'
import { nestingPadding } from 'utils/ArchUtil'

export interface IntakeRowProps {
  intake: ArchIntake
  isEnterprise: boolean
  isSuperAdmin: boolean
  unswoHeaderStyle?: React.CSSProperties
  isLockLoading: boolean
  actionButtons: React.ReactNode
  children?: React.ReactNode
  refEditTextareas: React.MutableRefObject<Map<string, HTMLTextAreaElement>>
  isReadOnly: boolean
  isAutoFocus: boolean
  onToggleLocked: () => void
  onDoubleClick: () => void
  onBlur: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onKeyUp: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onConfirmEdit: () => void
  onCancelEdit: () => void
}

export function IntakeRow({
  intake,
  isEnterprise,
  isSuperAdmin,
  unswoHeaderStyle,
  isLockLoading,
  actionButtons,
  children,
  refEditTextareas,
  isReadOnly,
  isAutoFocus,
  onToggleLocked,
  onDoubleClick,
  onBlur,
  onKeyDown,
  onKeyUp,
  onConfirmEdit,
  onCancelEdit,
}: IntakeRowProps) {
  const iconColor = isEnterprise ? 'white' : 'black'
  return (
    <React.Fragment>
      <li
        className="arch-editor-list-title arch-header-list-item"
        style={{ ...unswoHeaderStyle }}
      >
        <div style={{ paddingLeft: nestingPadding(1) }}>
          <div className="flex w-full flex-row items-center gap-2">
            <div
              className="flex w-[17px] min-w-[17px] cursor-pointer items-center justify-center hover:opacity-70"
              onClick={onToggleLocked}
            >
              {isSuperAdmin && isLockLoading && (
                <ButtonSpinner color={iconColor} />
              )}
              {isSuperAdmin && !isLockLoading && (
                <Svg
                  icon={intake.locked ? Icon.Lock : Icon.Unlock}
                  color={iconColor}
                />
              )}
            </div>
            <TextareaAutosize
              data-testid={`input-intake-name-${intake.id}`}
              ref={(el: HTMLTextAreaElement | null) => {
                if (el) refEditTextareas.current.set(`intake-${intake.id}`, el)
                else refEditTextareas.current.delete(`intake-${intake.id}`)
              }}
              className={`${
                isReadOnly
                  ? `arch-read-only ${
                      isEnterprise ? '!text-white' : '!text-black'
                    }`
                  : 'arch-add-input'
              }`}
              style={{ width: Math.min(intake.name.length + 1, 64) + 'ch' }}
              placeholder="Type intake name here"
              defaultValue={intake.name}
              readOnly={isReadOnly}
              autoFocus={isAutoFocus}
              onFocus={(e) => {
                const value = e.currentTarget.value
                e.currentTarget.value = ''
                e.currentTarget.value = value
              }}
              onDoubleClick={onDoubleClick}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
            />
            {!isReadOnly && (
              <InlineEditButtons
                onConfirm={onConfirmEdit}
                onCancel={onCancelEdit}
              />
            )}
            <Svg icon={Icon.Intake} color={iconColor} />
          </div>
          <div className="flex justify-end">{actionButtons}</div>
        </div>
      </li>
      {children}
    </React.Fragment>
  )
}
